import React, { useEffect, useState } from "react";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import {
  FaCrown,
  FaPlus,
  FaSignOutAlt,
  FaUserCheck,
  FaUserClock,
} from "react-icons/fa";
import { db } from "../../firebase";
import { useAuth } from "../../contexts/AuthContext";
import EditMemberModal from "./EditMemberModal";
import InviteMemberModal from "./InviteMemberModal";

const API_BASE_URL = `https://asia-southeast1-${process.env.REACT_APP_FIREBASE_PROJECT_ID}.cloudfunctions.net/api`;

const STATUS_LABELS = {
  Owner: "Owner",
  Active: "Active",
  pending: "Pending",
  declined: "Declined",
  left: "Left the circle",
};

const STATUS_CLASSES = {
  Owner: "bg-yellow-100 text-yellow-800",
  Active: "bg-green-100 text-green-800",
  pending: "bg-yellow-100 text-yellow-800",
  declined: "bg-red-100 text-red-800",
  left: "bg-gray-200 text-gray-800",
};

const FamilyCirclePanel = ({ profile }) => {
  const { currentUser, leaveSharedProfile } = useAuth();
  const [activeMembers, setActiveMembers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [members, setMembers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isInviteModalOpen, setInviteModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [memberBeingEdited, setMemberBeingEdited] = useState(null);

  const isOwner = !!profile && profile.userId === currentUser?.uid;

  useEffect(() => {
    if (!profile?.id || !currentUser || !isOwner) {
      setActiveMembers([]);
      setInvitations([]);
      setMembers([]);
      setIsLoading(false);
      return undefined;
    }

    setIsLoading(true);

    const membersRef = collection(
      db,
      "careProfiles",
      profile.id,
      "careTeamMembers",
    );
    const unsubscribeMembers = onSnapshot(
      query(membersRef),
      (snapshot) =>
        setActiveMembers(
          snapshot.docs.map((memberDoc) => ({
            ...memberDoc.data(),
            id: memberDoc.id,
            docId: memberDoc.id,
            status: memberDoc.data().status || "Active",
          })),
        ),
      (error) => {
        console.error("Error fetching family circle members:", error);
        setActiveMembers([]);
      },
    );

    const invitationsRef = collection(db, "invitations");
    const invitationsQuery = query(
      invitationsRef,
      where("careProfileId", "==", profile.id),
    );
    const unsubscribeInvitations = onSnapshot(
      invitationsQuery,
      (snapshot) =>
        setInvitations(
          snapshot.docs.map((invitationDoc) => {
            const data = invitationDoc.data();
            return {
              ...data,
              id: invitationDoc.id,
              email: data.inviteeEmail,
            };
          }),
        ),
      (error) => {
        console.error("Error fetching family circle invitations:", error);
        setInvitations([]);
      },
    );

    return () => {
      unsubscribeMembers();
      unsubscribeInvitations();
    };
  }, [currentUser, isOwner, profile?.id]);

  useEffect(() => {
    if (
      !profile?.id ||
      !currentUser ||
      !isOwner ||
      activeMembers.length === 0
    ) {
      return;
    }

    const refreshIdentities = async () => {
      try {
        const idToken = await currentUser.getIdToken();
        await fetch(`${API_BASE_URL}/care-team/refresh-member-identities`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify({ careProfileId: profile.id }),
        });
      } catch (error) {
        console.warn("Unable to refresh family circle identities:", error);
      }
    };

    refreshIdentities();
  }, [activeMembers.length, currentUser, isOwner, profile?.id]);

  useEffect(() => {
    if (!profile || !currentUser || !isOwner) {
      setIsLoading(false);
      return;
    }

    const activeMemberEmails = new Set(activeMembers.map((m) => m.email));
    const displayableStatus = new Set(["pending", "declined", "left"]);
    const nonActiveInvitations = invitations.filter(
      (invitation) =>
        !activeMemberEmails.has(invitation.email) &&
        displayableStatus.has(invitation.status),
    );

    const invitationsByEmail = nonActiveInvitations.reduce(
      (accumulator, invitation) => {
        if (
          !accumulator[invitation.email] ||
          accumulator[invitation.email].status !== "pending"
        ) {
          accumulator[invitation.email] = invitation;
        }
        return accumulator;
      },
      {},
    );

    const invitationMembers = Object.values(invitationsByEmail);
    const finalMembers = [...activeMembers, ...invitationMembers];
    const ownerInList = finalMembers.some(
      (member) =>
        member.id === currentUser.uid || member.email === currentUser.email,
    );

    if (!ownerInList) {
      finalMembers.unshift({
        id: currentUser.uid,
        docId: currentUser.uid,
        name:
          profile.profileName || currentUser.displayName || currentUser.email,
        email: currentUser.email,
        relationship: "Profile owner",
        role: "Owner",
        status: "Owner",
      });
    }

    setMembers(finalMembers);
    setIsLoading(false);
  }, [activeMembers, currentUser, invitations, isOwner, profile]);

  const handleApiAction = async (endpoint, body, failureMessage) => {
    setIsSubmitting(true);
    try {
      const idToken = await currentUser.getIdToken();
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        throw new Error(errorBody.error || failureMessage);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRemoveMember = async (member) => {
    if (
      !window.confirm(
        `Remove ${member.name || member.email} from this family circle?`,
      )
    ) {
      return;
    }

    try {
      await handleApiAction(
        "/care-team/remove-team-member",
        {
          careProfileId: profile.id,
          memberToRemoveId: member.id,
        },
        "Failed to remove member.",
      );
    } catch (error) {
      console.error("Error removing family circle member:", error);
      window.alert(`Failed to remove member: ${error.message}`);
    }
  };

  const handleRevokeInvitation = async (invitation) => {
    if (!window.confirm(`Revoke the invitation for ${invitation.email}?`)) {
      return;
    }

    try {
      await handleApiAction(
        "/care-team/revoke-invitation",
        {
          careProfileId: profile.id,
          invitationId: invitation.id,
        },
        "Failed to revoke invitation.",
      );
    } catch (error) {
      console.error("Error revoking family circle invitation:", error);
      window.alert(`Failed to revoke invitation: ${error.message}`);
    }
  };

  const handleDismissInvitation = async (item) => {
    if (!window.confirm(`Dismiss the invitation for ${item.email}?`)) {
      return;
    }

    try {
      if (item.status === "left") {
        await handleApiAction(
          "/care-team/remove-team-member",
          {
            careProfileId: profile.id,
            memberToRemoveId: item.id,
          },
          "Failed to dismiss member.",
        );
      } else {
        await handleApiAction(
          "/care-team/revoke-invitation",
          {
            careProfileId: profile.id,
            invitationId: item.id,
          },
          "Failed to dismiss invitation.",
        );
      }
    } catch (error) {
      console.error("Error dismissing family circle item:", error);
      window.alert(`Failed to dismiss: ${error.message}`);
    }
  };

  const handleLeaveCircle = async () => {
    if (
      !window.confirm(
        `Leave ${profile.profileName || "this profile"}'s family circle? You will lose access to this profile.`,
      )
    ) {
      return;
    }

    try {
      await leaveSharedProfile(profile.id);
    } catch (error) {
      console.error("Error leaving family circle:", error);
      window.alert(`Failed to leave circle: ${error.message}`);
    }
  };

  const handleInviteSent = () => {
    setInviteModalOpen(false);
  };

  if (!profile || !currentUser) {
    return null;
  }

  const isEmailAddress = (value = "") => /\S+@\S+\.\S+/.test(value);

  const getMemberDisplayName = (member) => {
    if (member.status === "Owner") {
      return profile.profileName || member.name || "Profile";
    }

    if (member.status === "pending") {
      return "Invited family member";
    }

    const candidateName = member.displayName || member.name;
    if (candidateName && !isEmailAddress(candidateName)) {
      return candidateName;
    }

    return "Family member";
  };

  const getMemberInitials = (member) => {
    const source = getMemberDisplayName(member) || member.email || "Member";
    const parts = source
      .split(/[.@\s_-]+/)
      .filter(Boolean)
      .slice(0, 2);

    return parts.map((part) => part[0]?.toUpperCase()).join("") || "M";
  };

  const getMemberAvatarUrl = (member) => {
    if (member.status === "Owner") {
      return profile.profilePictureURL || member.profilePictureURL || null;
    }

    return (
      member.profilePictureURL ||
      member.photoURL ||
      member.avatarURL ||
      member.photoUrl ||
      null
    );
  };

  const getStatusBadge = (member) => {
    if (member.status === "Owner") {
      return <FaCrown size={9} className="text-yellow-500" />;
    }
    if (member.status === "Active") {
      return <FaUserCheck size={9} className="text-green-600" />;
    }
    return <FaUserClock size={9} className="text-gray-500" />;
  };

  if (!isOwner) {
    return (
      <section
        id="family-circle"
        className="mb-8 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm sm:p-6"
      >
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <p className="text-sm font-medium uppercase tracking-wide text-gray-500">
              Family Circle
            </p>
            <h2 className="mt-1 text-2xl font-semibold text-gray-900">
              Shared with me
            </h2>
            <p className="mt-2 max-w-2xl text-sm text-gray-600">
              You can access this family health profile as{" "}
              <span className="font-semibold text-gray-900">
                {profile.role || "a family circle member"}
              </span>
              .
            </p>
          </div>
          <button
            onClick={handleLeaveCircle}
            className="inline-flex min-h-11 items-center justify-center rounded-lg border border-red-200 px-4 py-2 text-sm font-semibold text-red-700 transition hover:bg-red-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2"
          >
            <FaSignOutAlt className="mr-2" />
            Leave circle
          </button>
        </div>
      </section>
    );
  }

  return (
    <section id="family-circle" className="mb-8">
      <div className="mb-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-sm font-medium uppercase tracking-wide text-gray-500">
            Family Circle
          </p>
          <h2 className="text-2xl font-semibold text-gray-900">
            Who can help with this profile
          </h2>
          <p className="mt-1 text-sm text-gray-600">
            Invite trusted family members and manage their access to{" "}
            {profile.profileName || "this profile"}.
          </p>
        </div>
        <button
          onClick={() => setInviteModalOpen(true)}
          className="inline-flex min-h-11 items-center justify-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
        >
          <FaPlus className="mr-2" />
          Invite family
        </button>
      </div>

      {isLoading ? (
        <div className="rounded-2xl border border-gray-200 bg-white p-6 text-center text-gray-500 shadow-sm">
          Loading family circle...
        </div>
      ) : members.length === 0 ? (
        <div className="rounded-2xl border border-gray-200 bg-white p-6 text-center shadow-sm">
          <p className="text-gray-600">
            This family circle does not have any members yet.
          </p>
          <p className="mt-1 text-sm text-gray-500">
            Use Invite family to get started.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {members.map((member) => (
            <article
              key={`${member.status}-${member.id}`}
              className="flex min-h-full flex-col justify-between rounded-2xl border border-gray-200 bg-white p-5 shadow-sm"
            >
              <div>
                <div className="flex items-start gap-4">
                  <div className="relative h-12 w-12 flex-shrink-0">
                    {getMemberAvatarUrl(member) ? (
                      <img
                        src={getMemberAvatarUrl(member)}
                        alt={getMemberDisplayName(member)}
                        className="h-12 w-12 rounded-full object-cover ring-2 ring-blue-100"
                      />
                    ) : (
                      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-sm font-semibold text-slate-600 ring-2 ring-blue-100">
                        {getMemberInitials(member)}
                      </div>
                    )}
                    <span className="absolute -bottom-0.5 -right-0.5 flex h-4.5 w-4.5 items-center justify-center rounded-full border border-white bg-white shadow-sm">
                      {getStatusBadge(member)}
                    </span>
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {getMemberDisplayName(member)}
                    </h3>
                    <p className="break-all text-sm text-gray-500">
                      {member.email}
                    </p>
                  </div>
                </div>
                <div className="mt-4 flex flex-wrap items-center gap-2">
                  <span
                    className={`rounded-full px-3 py-1 text-xs font-semibold ${
                      STATUS_CLASSES[member.status] ||
                      "bg-gray-100 text-gray-700"
                    }`}
                  >
                    {STATUS_LABELS[member.status] || member.status}
                  </span>
                  <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700">
                    Role: {member.role}
                  </span>
                  <span className="rounded-full bg-purple-50 px-3 py-1 text-xs font-semibold text-purple-700">
                    Relationship: {member.relationship || "Not set"}
                  </span>
                </div>
              </div>

              {member.status !== "Owner" && (
                <div className="mt-5 flex flex-wrap justify-end gap-3 border-t border-gray-100 pt-4">
                  {member.status === "Active" && (
                    <>
                      <button
                        onClick={() => setMemberBeingEdited(member)}
                        disabled={isSubmitting}
                        className="min-h-11 rounded-lg px-3 py-2 text-sm font-semibold text-blue-700 transition hover:bg-blue-50 disabled:opacity-50"
                      >
                        Edit member
                      </button>
                      <button
                        onClick={() => handleRemoveMember(member)}
                        disabled={isSubmitting}
                        className="min-h-11 rounded-lg px-3 py-2 text-sm font-semibold text-red-700 transition hover:bg-red-50 disabled:opacity-50"
                      >
                        Remove
                      </button>
                    </>
                  )}
                  {member.status === "pending" && (
                    <button
                      onClick={() => handleRevokeInvitation(member)}
                      disabled={isSubmitting}
                      className="min-h-11 rounded-lg px-3 py-2 text-sm font-semibold text-red-700 transition hover:bg-red-50 disabled:opacity-50"
                    >
                      Revoke invite
                    </button>
                  )}
                  {(member.status === "declined" ||
                    member.status === "left") && (
                    <button
                      onClick={() => handleDismissInvitation(member)}
                      disabled={isSubmitting}
                      className="min-h-11 rounded-lg px-3 py-2 text-sm font-semibold text-gray-700 transition hover:bg-gray-100 disabled:opacity-50"
                    >
                      Dismiss
                    </button>
                  )}
                </div>
              )}
            </article>
          ))}
        </div>
      )}

      {isInviteModalOpen && (
        <InviteMemberModal
          isOpen={isInviteModalOpen}
          onClose={() => setInviteModalOpen(false)}
          onInviteSent={handleInviteSent}
          careProfileId={profile.id}
          careProfileName={profile.profileName}
        />
      )}

      <EditMemberModal
        isOpen={!!memberBeingEdited}
        member={memberBeingEdited}
        onClose={() => setMemberBeingEdited(null)}
        activeCareProfileId={profile.id}
      />
    </section>
  );
};

export default FamilyCirclePanel;
